import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * Public branding manifest for the current host.
 *
 * GET /branding is unauthenticated. The Laravel ResolveCustomDomain
 * global middleware binds `currentAgency` from the Host header so that
 * white-label custom domains return their tenant's logo/colors. On the
 * canonical aiads.trustluna.com host, the response falls back to the
 * default AIAds branding (`is_white_label: false`).
 */
export interface BrandingManifest {
  agency_id: number | null;
  agency_name: string;
  logo_url: string | null;
  favicon_url: string | null;
  primary_color: string;
  accent_color: string;
  support_email: string | null;
  support_phone: string | null;
  footer_text: string | null;
  locale: string;
  is_white_label: boolean;
}

const DEFAULT_BRANDING: BrandingManifest = {
  agency_id: null,
  agency_name: 'AIAds',
  logo_url: null,
  favicon_url: null,
  primary_color: '#2E5BFF',
  accent_color: '#7C3AED',
  support_email: null,
  support_phone: null,
  footer_text: null,
  locale: 'pl',
  is_white_label: false,
};

/**
 * Convert a #RRGGBB hex into a "h s% l%" Tailwind HSL CSS-var triple.
 * The index.css `--brand` token is consumed in HSL format.
 */
function hexToHslTriple(hex: string): string | null {
  const clean = hex.replace('#', '').trim();
  if (clean.length !== 6) return null;
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  if ([r, g, b].some(Number.isNaN)) return null;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Shift a hex color toward white (positive percent) or black (negative).
 * Used to derive the brand-50/100/600/700 shades when the agency only
 * provides a single primary color.
 */
function shadeHex(hex: string, percent: number): string {
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return hex;
  const num = parseInt(clean, 16);
  if (Number.isNaN(num)) return hex;
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;
  const t = percent < 0 ? 0 : 255;
  const p = Math.abs(percent) / 100;
  r = Math.round((t - r) * p + r);
  g = Math.round((t - g) * p + g);
  b = Math.round((t - b) * p + b);
  const hexPair = (n: number) => n.toString(16).padStart(2, '0');
  return `#${hexPair(r)}${hexPair(g)}${hexPair(b)}`;
}

/**
 * Hook used at the top of the App tree. Fetches the public branding
 * manifest exactly once, applies CSS variables to the document root,
 * sets the document title and favicon. Returns the manifest so child
 * components can read `agency_name`, `logo_url`, etc.
 */
export function useBranding(): BrandingManifest {
  const query = useQuery<BrandingManifest>({
    queryKey: ['branding-manifest'],
    queryFn: async () => {
      try {
        const res = await api.get('/branding');
        return (res.data?.data ?? DEFAULT_BRANDING) as BrandingManifest;
      } catch {
        return DEFAULT_BRANDING;
      }
    },
    staleTime: 30 * 60_000,
    retry: false,
  });

  const branding = useMemo(() => query.data ?? DEFAULT_BRANDING, [query.data]);

  useEffect(() => {
    const root = document.documentElement;
    const primary = branding.primary_color || DEFAULT_BRANDING.primary_color;
    const hslTriple = hexToHslTriple(primary);
    if (hslTriple) {
      root.style.setProperty('--brand', hslTriple);
      root.style.setProperty('--ring', hslTriple);
    }
    root.style.setProperty('--color-primary', primary);
    root.style.setProperty('--color-accent', branding.accent_color || DEFAULT_BRANDING.accent_color);
    root.style.setProperty('--brand-50', shadeHex(primary, 95));
    root.style.setProperty('--brand-100', shadeHex(primary, 85));
    root.style.setProperty('--brand-500', primary);
    root.style.setProperty('--brand-600', shadeHex(primary, -15));
    root.style.setProperty('--brand-700', shadeHex(primary, -30));

    if (branding.is_white_label && branding.agency_name) {
      document.title = branding.agency_name;
    }

    if (branding.favicon_url) {
      let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = branding.favicon_url;
    }
  }, [branding]);

  return branding;
}
